/**
 * @fileOverview This file contains the implementation of the ReportDesigner component for the Reports builder
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

//#region Imports
import '../../scss/_pdfme.scss';
import { Component } from 'react';
import { Designer } from '@pdfme/ui';
import { BLANK_PDF, PDFME_VERSION } from '@pdfme/common';
import { text, table, image, line, rectangle, ellipse } from '@pdfme/schemas';
import axios from 'axios';
import { GlobalVariables } from '../../globalVar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBroom,
  faChevronLeft,
  faFileCircleMinus,
  faFileCirclePlus,
} from '@fortawesome/pro-duotone-svg-icons';
import { Box, IconButton, Tooltip } from '@mui/material';
import { MuiFileInput } from 'mui-file-input';
import { AttachFileOutlined as AttachFileIcon } from '@mui/icons-material';
import RobotoR from '../../media/Fonts/Roboto/Roboto-Regular.ttf';
import RobotoB from '../../media/Fonts/Roboto/Roboto-Bold.ttf';
import RobotoI from '../../media/Fonts/Roboto/Roboto-Italic.ttf';
import RobotoBI from '../../media/Fonts/Roboto/Roboto-BoldItalic.ttf';
import NoticiaTextR from '../../media/Fonts/NoticiaText/NoticiaText-Regular.ttf';
import NoticiaTextB from '../../media/Fonts/NoticiaText/NoticiaText-Bold.ttf';
import NoticiaTextI from '../../media/Fonts/NoticiaText/NoticiaText-Italic.ttf';
import NoticiaTextBI from '../../media/Fonts/NoticiaText/NoticiaText-BoldItalic.ttf';
import InriaSerifR from '../../media/Fonts/InriaSerif/InriaSerif-Regular.ttf';
import InriaSerifB from '../../media/Fonts/InriaSerif/InriaSerif-Bold.ttf';
import InriaSerifI from '../../media/Fonts/InriaSerif/InriaSerif-Italic.ttf';
import InriaSerifBI from '../../media/Fonts/InriaSerif/InriaSerif-BoldItalic.ttf';
import { getToast } from '../../utils/toastConfig';
//#endregion

/**
 * Generates the font URL based on the given path.
 * @param {string} path - The path to the font file.
 * @returns {string} The full URL to the font file.
 */
const getFontUrl = (fileName) => {
  const baseUrl = 'https://durandaldabia9bc.blob.core.windows.net/fonts/';
  return `${baseUrl}${fileName}`;
};

const font = {
  Roboto: {
    data: getFontUrl('Roboto-Regular.ttf'),
    fallback: true,
  },
  RobotoBold: {
    data: getFontUrl('Roboto-Bold.ttf'),
  },
  RobotoItalic: {
    data: getFontUrl('Roboto-Italic.ttf'),
  },
  RobotoBoldItalic: {
    data: getFontUrl('Roboto-BoldItalic.ttf'),
  },
  NoticiaText: {
    data: getFontUrl('NoticiaText-Regular.ttf'),
  },
  NoticiaTextBold: {
    data: getFontUrl('NoticiaText-Bold.ttf'),
  },
  NoticiaTextItalic: {
    data: getFontUrl('NoticiaText-Italic.ttf'),
  },
  NoticiaTextBoldItalic: {
    data: getFontUrl('NoticiaText-BoldItalic.ttf'),
  },
  InriaSerif: {
    data: getFontUrl('InriaSerif-Regular.ttf'),
  },
  InriaSerifBold: {
    data: getFontUrl('InriaSerif-Bold.ttf'),
  },
  InriaSerifItalic: {
    data: getFontUrl('InriaSerif-Italic.ttf'),
  },
  InriaSerifBoldItalic: {
    data: getFontUrl('InriaSerif-BoldItalic.ttf'),
  },
  Bahamas: {
    data: getFontUrl('bahamasn.TTF'),
  },
  BahamasBold: {
    data: getFontUrl('bahamasb.TTF'),
  },
};

class ReportDesigner extends Component {
  state = {
    file: null,
  };

  componentDidMount() {
    let template = JSON.parse(localStorage.getItem('reportBuilderTemplate'));

    if (!template) {
      template = {
        basePdf: {
          width: 210,
          height: 297,
          padding: [0, 0, 0, 0],
        },
        schemas: [],
        pdfmeVersion: PDFME_VERSION,
      };
    }

    if (!template.basePdf) {
      template.basePdf = {
        width: 210,
        height: 297,
        padding: [0, 0, 0, 0],
      };
    }

    this.designer = new Designer({
      domContainer: this.domContainer,
      template: template,
      plugins: {
        table,
        text,
        image,
        line,
        rectangle,
        ellipse,
      },
      options: {
        font: font,
      },
    });
    this.forceUpdate();
  }

  /**
   * Handles the saving of the current template.
   * @async
   */
  handleSaveTemplate = async () => {
    const template = this.designer.getTemplate();
    const reportId = localStorage.getItem('reportBuilderReportId');

    try {
      console.log(template);
      const response = await axios.patch(
        `${GlobalVariables.getURL()}updateReport/${reportId}`,
        template,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      if (response.data.status === 'success') {
        getToast({
          status: 'success',
          title: 'Reporte actualizado exitosamente.',
        });
      } else {
        getToast({
          status: 'error',
          title: 'Error al actualizar el reporte. Vuelve a intentarlo.',
        });
      }
    } catch (error) {
      getToast({ status: 'error', title: error.message });
    }
  };

  /**
   * Handles the loading of a basePDF file.
   * @param {File} file - The basePDF file to be loaded.
   */
  handleLoadPdf = (file) => {
    this.setState({ file: file }, () => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64pdf = reader.result;
        const currTemplate = this.designer.getTemplate();
        currTemplate.basePdf = base64pdf;
        this.designer.updateTemplate(currTemplate);
        this.forceUpdate();
      };
      reader.readAsDataURL(file);
    });
  };

  /**
   * Handles the addition of a new page to the template.
   */
  handleAddPage = () => {
    const currTemplate = this.designer.getTemplate();
    currTemplate.schemas.push(currTemplate.schemas[0]);
    this.designer.updateTemplate(currTemplate);
    this.forceUpdate();
  };

  /**
   * Handles the removal of the last page from the template.
   */
  handleRemovePage = () => {
    const currTemplate = this.designer.getTemplate();
    currTemplate.schemas.pop();
    this.designer.updateTemplate(currTemplate);
    this.forceUpdate();
  };

  /**
   * Handles the clearing of the current basePDF template.
   */
  handleClearPdf = () => {
    const currTemplate = this.designer.getTemplate();
    currTemplate.basePdf = BLANK_PDF;
    this.designer.updateTemplate(currTemplate);
    this.forceUpdate();
  };

  render() {
    const { translations } = this.props;

    return (
      <Box sx={{ height: '100%', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '5%',
            width: '100%',
            marginBottom: '1%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-evenly',
              gap: 3,
            }}
          >
            <Box>
              <IconButton
                size="medium"
                className="back-icon-container"
                onClick={() => this.props.navigate('/config')}
              >
                <FontAwesomeIcon icon={faChevronLeft} className="back-icon" />
              </IconButton>

              <MuiFileInput
                variant="outlined"
                size="small"
                value={this.state.file ? this.state.file.name : null}
                placeholder={translations.builderDSelectBasePdf}
                title="Solo se permiten archivos PDF"
                InputProps={{
                  inputProps: { accept: '.pdf' },
                  startAdornment: <AttachFileIcon />,
                }}
                onChange={this.handleLoadPdf}
              />
            </Box>

            <Box>
              <Tooltip title={translations.builderDAddPage} arrow>
                <IconButton
                  size="medium"
                  className="report-builder-addpage-icon-container"
                  onClick={this.handleAddPage}
                >
                  <FontAwesomeIcon icon={faFileCirclePlus} />
                </IconButton>
              </Tooltip>

              {this.designer && (
                <Tooltip title={translations.builderDRemovePage} arrow>
                  <IconButton
                    size="medium"
                    className="report-builder-removepage-icon-container"
                    onClick={this.handleRemovePage}
                    disabled={this.designer.getTemplate().schemas.length < 2}
                  >
                    <FontAwesomeIcon icon={faFileCircleMinus} />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={translations.builderDClearBasePdf} arrow>
                <IconButton
                  size="medium"
                  className="report-builder-clearbg-icon-container"
                  onClick={this.handleClearPdf}
                >
                  <FontAwesomeIcon icon={faBroom} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <button
            className="save-report-button"
            onClick={this.handleSaveTemplate}
          >
            {translations.builderDSave}
          </button>
        </Box>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            height: '94%',
            width: '100%',
          }}
          ref={(domContainer) => {
            this.domContainer = domContainer;
          }}
        />
      </Box>
    );
  }
}

export default ReportDesigner;
